// ============================================================================
// REQUOTE SCOPE EXTRACTOR (v5.38.0)
// ----------------------------------------------------------------------------
// Stage 1.5 → Stage 4 shortcut when Project Summary detected a re-quote.
//
// Reads the previous-quote PDF text (already OCR'd at Stage 1 import — sits
// on documents[i].content), reads the change-request summary captured by
// Project Summary, asks the AI to extract scope items from the previous
// quote AND apply the change-request in one pass. Returns scope items ready
// to seed Stage 4 (Scope Builder), bypassing Stage 2 (Risk Analysis) + Stage
// 3 (Work Method Statement).
//
// Per Peter 2026-05-21: prices CARRY OVER from the previous quote; Stage 4
// surfaces a stale-pricing banner. Change-request edits are APPLIED
// AUTOMATICALLY; modified/added/removed items are tagged so the UI shows
// chips and the user can revert per-item.
//
// Wires through the backend gateway (callOpenRouter, no user-managed keys)
// and pickModelForTask so the model is registry-driven.
//
// Kill switch: window.qm_disable_requote_shortcut = true at any time disables
// the shortcut path entirely; the UI banner hides and the user proceeds
// through the normal Stage 2/3 pipeline.
// ============================================================================

import { callOpenRouter, getMaxOutputTokens } from './ai-api';
import { pickModelForTask } from './model-selector';
import { getPrompt, getConfigValue } from '../config/pipeline-registry';
import { REQUOTE_SCOPE_EXTRACTION_PROMPT } from '../config/prompts';
import { parseJsonSafe } from '../utils/json-parse-safe';
import { assemblePrompt } from '../utils/prompt-assembly';
import { genItemId } from '../utils/item-id';
import { aiDocumentFormat } from '../utils/ai-document-label';
import {
    loadAssistantBusinessIdentity,
    NEUTRAL_BUSINESS_NAME,
    RJ_BIRD_ASSISTANT_BUSINESS_DESCRIPTION,
    RJ_BIRD_ASSISTANT_BUSINESS_NAME,
} from './business-assistant-name';

/**
 * The two business slots of REQUOTE_SCOPE_EXTRACTION_PROMPT (F9). RJ Bird keeps
 * the words the prompt always used. Any other business gets its own name, and
 * an unknown business is never named, so RJ Bird's name never reaches another
 * business's re-quote. RJ Bird is the business whose identity carries RJ Bird's
 * own description, which only RJ Bird's profile produces.
 *
 * @param {{ name: string, description: string }} identity
 */
export function requoteBusinessSlots(identity) {
    const name = identity?.name || NEUTRAL_BUSINESS_NAME;
    if (name === RJ_BIRD_ASSISTANT_BUSINESS_NAME
        && identity?.description === RJ_BIRD_ASSISTANT_BUSINESS_DESCRIPTION) {
        return {
            BUSINESS_IDENTITY: 'RJ Bird Building Pty Ltd, Customer Zero',
            BUSINESS_QUOTE_PDF: 'RJ Bird quote PDF',
        };
    }
    return {
        BUSINESS_IDENTITY: name,
        BUSINESS_QUOTE_PDF: name === NEUTRAL_BUSINESS_NAME ? 'quote PDF' : `${name} quote PDF`,
    };
}

/**
 * The four valid origins for a re-quote-extracted scope item. Anything else
 * is normalised to 'carried_over' (safest default — treats unknown rows as
 * unchanged from the previous quote rather than dropping them silently).
 */
export const REQUOTE_ORIGINS = Object.freeze(['carried_over', 'modified', 'added', 'removed']);

/**
 * Locate the previous-quote document in the project's documents[] array by
 * filename (the value the AI returned in requote_signal.previous_quote_doc_name).
 * Falls back to a case-insensitive substring match if exact-match misses, so a
 * minor OCR/encoding difference in the filename doesn't break the path.
 *
 * Returns { doc, index } when found, null otherwise.
 */
export function findPreviousQuoteDoc(documents, previousQuoteDocName) {
    if (!Array.isArray(documents) || documents.length === 0) return null;
    if (!previousQuoteDocName || typeof previousQuoteDocName !== 'string') return null;
    const needle = previousQuoteDocName.trim();
    if (!needle) return null;

    const exactIdx = documents.findIndex(d => String(d?.name || '') === needle);
    if (exactIdx >= 0) return { doc: documents[exactIdx], index: exactIdx };

    const lowerNeedle = needle.toLowerCase();
    const ciExactIdx = documents.findIndex(d => String(d?.name || '').toLowerCase() === lowerNeedle);
    if (ciExactIdx >= 0) return { doc: documents[ciExactIdx], index: ciExactIdx };

    const partialIdx = documents.findIndex(d => String(d?.name || '').toLowerCase().includes(lowerNeedle));
    if (partialIdx >= 0) return { doc: documents[partialIdx], index: partialIdx };

    return null;
}

/**
 * Pull the OCR'd text off a document. Stage 1 stores extracted text on
 * `content`; older imports may have used `.text` or `.extractedText`. Returns
 * '' when no usable text is present so the caller can decide whether to
 * proceed or fall back to the normal pipeline.
 */
export function getDocumentText(doc) {
    if (!doc) return '';
    const candidates = [doc.content, doc.text, doc.extractedText];
    for (const c of candidates) {
        if (typeof c === 'string' && c.trim().length > 0) return c;
    }
    return '';
}

/**
 * Normalise one AI-emitted scope item into the shape Stage 4 expects.
 * Defensive: missing/invalid fields fall back to safe defaults rather than
 * throwing, so a partial / sloppy extraction still produces something
 * editable in Stage 4. The user reviews the result anyway.
 */
function normaliseExtractedItem(raw, fallbackIndex) {
    const requoteOrigin = REQUOTE_ORIGINS.includes(raw?.requoteOrigin)
        ? raw.requoteOrigin
        : 'carried_over';

    const amount = Number(raw?.amount);
    const safeAmount = Number.isFinite(amount) ? Math.max(0, amount) : 0;

    const amountConfidence = Number(raw?.amountConfidence);
    const safeConfidence = Number.isFinite(amountConfidence)
        ? Math.max(0, Math.min(100, amountConfidence))
        : (requoteOrigin === 'carried_over' ? 90 : 50);

    const origVals = raw?.requoteOriginalValues || {};
    return {
        id: genItemId(),
        title: typeof raw?.title === 'string' && raw.title.trim() ? raw.title.trim() : `Untitled item ${fallbackIndex + 1}`,
        description: typeof raw?.description === 'string' ? raw.description : '',
        amount: safeAmount,
        // Stage 4 uses these fields elsewhere; populate with sane defaults so
        // the seeded scope doesn't trip downstream "missing field" assertions.
        category: '',
        unit: '',
        quantity: 1,
        inclusions: [],
        images: [],
        amountLocked: false,
        // Re-quote-specific metadata. Stage 4 reads these to render chips +
        // the per-row "revert to original" affordance for modified items.
        requoteOrigin,
        requoteAmountConfidence: safeConfidence,
        requoteOriginalValues: requoteOrigin === 'modified' ? {
            title: typeof origVals.title === 'string' ? origVals.title : null,
            description: typeof origVals.description === 'string' ? origVals.description : null,
            amount: Number.isFinite(Number(origVals.amount)) ? Number(origVals.amount) : null,
        } : { title: null, description: null, amount: null },
        requoteNote: typeof raw?.requoteNote === 'string' ? raw.requoteNote : '',
    };
}

/**
 * Validate + normalise the parsed JSON. Always returns a usable shape even
 * if the AI emitted something incomplete — the caller can decide whether to
 * proceed or surface a "couldn't parse cleanly" toast.
 */
export function normaliseExtractionResult(parsed) {
    const ok = parsed && typeof parsed === 'object' && Array.isArray(parsed.items);
    if (!ok) {
        return {
            ok: false,
            extractionNotes: 'Extractor returned an invalid shape — falling back to empty scope. User should run normal pipeline or edit manually in Stage 4.',
            items: [],
            unresolvedChangeInstructions: [],
            stalePricingWarning: false,
            stalePricingReason: '',
            previousQuoteDate: null,
        };
    }
    const items = parsed.items.map((raw, idx) => normaliseExtractedItem(raw, idx));
    return {
        ok: true,
        extractionNotes: typeof parsed.extraction_notes === 'string' ? parsed.extraction_notes : '',
        items,
        unresolvedChangeInstructions: Array.isArray(parsed.unresolved_change_instructions)
            ? parsed.unresolved_change_instructions.filter(s => typeof s === 'string' && s.trim()).slice(0, 20)
            : [],
        // The AI's non-age "prices need review" flag (kept as an OR input to the
        // deterministic assessment below — see assessRequoteStaleness). The
        // model must NOT decide staleness from age; it reads the printed date.
        stalePricingWarning: parsed.stale_pricing_warning === true,
        stalePricingReason: typeof parsed.stale_pricing_reason === 'string' ? parsed.stale_pricing_reason : '',
        // The date PRINTED on the previous quote, as the AI read it (ISO
        // yyyy-mm-dd, or null). Reading a printed date is reliable; computing
        // elapsed days is not — so the app does the age math (assessRequoteStaleness).
        previousQuoteDate: typeof parsed.previous_quote_date === 'string' && parsed.previous_quote_date.trim()
            ? parsed.previous_quote_date.trim()
            : null,
    };
}

/**
 * Deterministic staleness assessment for the Stage 4 re-quote banner.
 *
 * WHY THIS EXISTS: the AI was authoring the whole banner sentence and getting
 * the date arithmetic wrong — e.g. calling a quote dated 23 May "more than 90
 * days old" when it was ~45 days old (observed live 2026-07-07). Per the same
 * philosophy as resolveValidity() ("AI never authors a date/validity string"),
 * the model now only REPORTS the printed date (previousQuoteDate) and this pure
 * helper owns the elapsed-days math + the user-facing wording.
 *
 * @param {Object} a
 * @param {string|null} [a.previousQuoteDate]  ISO date the AI read off the quote
 * @param {Array}       [a.activeItems]        partitioned active scope items
 * @param {Array}       [a.removedHistory]     partitioned removed items
 * @param {boolean}     [a.aiWarning]          AI's explicit (non-age) review flag
 * @param {number}      [a.nowMs]              injectable clock (tests)
 * @returns {{ stalePricingWarning: boolean, stalePricingReason: string, daysSince: number|null }}
 */
export function assessRequoteStaleness({
    previousQuoteDate = null,
    activeItems = [],
    removedHistory = [],
    aiWarning = false,
    nowMs = Date.now(),
} = {}) {
    const d = previousQuoteDate ? new Date(previousQuoteDate) : null;
    const valid = !!(d && !Number.isNaN(d.getTime()));
    const daysSince = valid ? Math.max(0, Math.floor((nowMs - d.getTime()) / 86400000)) : null;
    const ageStale = daysSince != null && daysSince > 90;
    const scopeModified =
        (Array.isArray(removedHistory) && removedHistory.length > 0)
        || (Array.isArray(activeItems) && activeItems.some(it => it?.requoteOrigin === 'modified' || it?.requoteOrigin === 'added'));
    const stalePricingWarning = ageStale || scopeModified || !!aiWarning;

    let stalePricingReason = '';
    if (stalePricingWarning) {
        const parts = [];
        if (valid) {
            const dateStr = d.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
            if (ageStale) {
                parts.push(`The original quote is dated ${dateStr} — over 90 days old, so pricing may be out of date`);
            } else {
                const age = daysSince === 0 ? 'today'
                    : daysSince === 1 ? 'yesterday'
                    : daysSince < 60 ? `${daysSince} days ago`
                    : `about ${Math.round(daysSince / 30)} months ago`;
                parts.push(`The original quote is dated ${dateStr} (${age})`);
            }
        }
        if (scopeModified) parts.push('the scope has been modified');
        if (parts.length) {
            const joined = parts.join(', and ');
            stalePricingReason = joined.charAt(0).toUpperCase() + joined.slice(1) + '.';
        } else {
            // aiWarning-only, no readable date: fall back to the AI's own
            // (non-age) reason if it gave one, else a neutral prompt.
            stalePricingReason = 'Verify pricing before sending.';
        }
    }
    return { stalePricingWarning, stalePricingReason, daysSince };
}

/**
 * Filter the extracted items into the "active" scope (carried_over + modified
 * + added) that Stage 4 should display, plus the removed-history (so the UI
 * can show a "previously included" section + offer revert). Stage 4 prices
 * + UI render from `active`; `removedHistory` is kept on formData for audit.
 */
export function partitionByRequoteOrigin(items) {
    const active = [];
    const removedHistory = [];
    for (const it of (Array.isArray(items) ? items : [])) {
        if (it?.requoteOrigin === 'removed') removedHistory.push(it);
        else active.push(it);
    }
    return { active, removedHistory };
}

/**
 * Main entry point. Builds the prompt, runs the AI call via the backend
 * gateway, parses + normalises the result.
 *
 * @param {Object} args
 * @param {string} args.apiKey
 * @param {string} args.model              - Global model fallback (registry resolves task model)
 * @param {Object} args.previousQuoteDoc   - documents[i] entry the AI flagged
 * @param {string} args.changeRequestSummary - Project Summary's requote_signal.change_request_summary
 * @param {string} [args.additionalNotes]    - formData.notes (operator-typed inspector notes)
 * @param {string} [args.projectId]
 * @param {AbortSignal} [args.signal]
 * @returns {Promise<{ ok, items, unresolvedChangeInstructions, stalePricingWarning, stalePricingReason, extractionNotes, modelUsed }>}
 */
export async function extractRequoteScope({
    apiKey,
    model,
    previousQuoteDoc,
    changeRequestSummary,
    additionalNotes = '',
    projectId = null,
    signal,
}) {
    if (!apiKey) throw new Error('API key is required for re-quote extraction');
    if (!previousQuoteDoc) throw new Error('previousQuoteDoc is required');

    const previousQuoteText = getDocumentText(previousQuoteDoc);
    if (!previousQuoteText) {
        throw new Error(`Previous quote document "${previousQuoteDoc?.name || 'unknown'}" has no extracted text — Stage 1 OCR may have failed at import. Re-import the document or run the normal pipeline.`);
    }

    const promptTemplate = getPrompt('requote-scope-extraction') || REQUOTE_SCOPE_EXTRACTION_PROMPT;
    // Cap previous-quote text at 40k chars to keep prompt cost bounded; real
    // RJ Bird quotes are typically <8k chars so this is a safety net for
    // accidental concatenations.
    const cappedQuoteText = previousQuoteText.length > 40000
        ? previousQuoteText.slice(0, 40000) + '\n[...truncated; full text was ' + previousQuoteText.length + ' chars]'
        : previousQuoteText;
    const { prompt } = assemblePrompt(promptTemplate, {
        ...requoteBusinessSlots(await loadAssistantBusinessIdentity()),
        // CN5: the slot keeps its old name for saved prompt overrides, but it is
        // filled with the file format, never the file name.
        PREVIOUS_QUOTE_FILENAME: aiDocumentFormat(previousQuoteDoc) || 'document',
        PREVIOUS_QUOTE_TEXT: cappedQuoteText,
        CHANGE_REQUEST_SUMMARY: changeRequestSummary || '(no explicit change request — the client may want the same quote re-issued; carry every line over verbatim)',
        ADDITIONAL_NOTES: (additionalNotes || '').slice(0, 4000) || '(none)',
    });

    const taskModel = pickModelForTask('requote-scope-extraction', model);
    const temperature = getConfigValue('requote-scope-extraction', 'temperature') ?? 0.2;
    const maxTokens = getMaxOutputTokens(taskModel);

    let rawResponse;
    try {
        rawResponse = await callOpenRouter({
            apiKey,
            model: taskModel,
            messages: [{ role: 'user', content: prompt }],
            temperature,
            maxTokens,
            responseFormat: { type: 'json_object' },
            task: 'requote-scope-extraction',
            pipelineId: 'requote-scope-extraction',
            stageName: 'Stage 1.5 → Stage 4 (re-quote shortcut)',
            projectId,
            signal,
        });
    } catch (err) {
        const wrapped = new Error(`Re-quote extraction AI call failed: ${err?.message || 'unknown error'}`);
        wrapped.cause = err;
        throw wrapped;
    }

    const parsed = parseJsonSafe(rawResponse);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        const error = new Error('Re-quote extraction returned incomplete or malformed JSON. Retry the extraction; no scope items were applied.');
        error.code = 'REQUOTE_EXTRACTION_INVALID_JSON';
        throw error;
    }
    const result = normaliseExtractionResult(parsed);
    return {
        ...result,
        modelUsed: taskModel,
        previousQuoteDocName: previousQuoteDoc?.name || null,
        generatedAt: new Date().toISOString(),
    };
}
